export const BOOKING_STATUS = {
    PLACED: 'PLACED',
    CONFIRMED: 'CONFIRMED',
    OUT_FOR_SERVICE: 'OUT_FOR_SERVICE',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
} as const;

export type BookingStatus = typeof BOOKING_STATUS[keyof typeof BOOKING_STATUS];

export const PAYMENT_STATUS = {
    PENDING: 'pending',
    PAID: 'paid',
    REFUNDED: 'refunded',
    CANCELLED: 'cancelled',
} as const;

export type PaymentStatus = typeof PAYMENT_STATUS[keyof typeof PAYMENT_STATUS];

/**
 * Flat fee retained from the customer's prepaid amount when a booking is
 * cancelled. Applies only to bookings where the customer committed money
 * upfront via Razorpay — Pay-in-Full (`full_payment`) and 10% Advance
 * (`advance_payment`). COD bookings never collected anything upfront, so
 * there is no money to deduct from.
 *
 * Wired through:
 *   - `computeBookingCancellationFee` (single source of truth below)
 *   - `initiateRefunds` in src/lib/bookingCancellation.ts (deducts before refund)
 *   - Cancellation email (`generateBookingCancellationEmail` in src/lib/email.ts)
 *   - Customer cancel-confirm dialog (CustomerDashboardInteractive.tsx)
 *   - Refund policy page (src/app/refunds/page.tsx)
 */
export const BOOKING_CANCELLATION_FEE_INR = 500;

/**
 * Pure function — given the booking's payment context, returns the rupee
 * amount that should be retained as a cancellation fee. Returns 0 when the
 * fee does not apply (COD, unpaid, or unknown payment type). Caps at the
 * actually-paid amount so we never try to retain more than what the
 * customer committed.
 *
 * Kept payment-type-aware (not blanket on `paidAmount > 0`) because
 * reschedule surcharge or technician scope-up payments can land on a COD
 * booking — those shouldn't trigger the cancellation fee.
 */
export function computeBookingCancellationFee(
    paymentType: string | undefined | null,
    paidAmount: number,
): number {
    if (!paidAmount || paidAmount <= 0) return 0;
    const pt = String(paymentType || '').toLowerCase();
    const isPrepaid = pt === 'full_payment' || pt === 'advance_payment';
    if (!isPrepaid) return 0;
    return Math.min(BOOKING_CANCELLATION_FEE_INR, paidAmount);
}

/**
 * Discount applied to the SERVICES portion (items + add-ons, before travel &
 * surge) when the customer pays the full amount upfront (`full_payment`). The
 * travel fee is ALSO waived for full_payment, but that waiver is handled in the
 * pricing calc — this constant is only the services-discount rate.
 *
 * 10% Advance (`advance_payment`) and COD customers do NOT get this discount.
 *
 * This is the money source of truth — keep every computation pointed at it:
 *   - Server pricing:        src/app/api/bookings/route.ts (primary + combined)
 *   - Customer live preview: service-booking-flow/BookingFlowInteractive.tsx
 *   - Manual booking preview: admin/bookings/new/CreateBookingForm.tsx
 * The rendered "(10%)" labels next to each breakdown must match this rate.
 */
export const PAY_IN_FULL_DISCOUNT_RATE = 0.1;

/**
 * Magia's single registered / business address (operated by Delta Solutions).
 * This is THE one source of truth — used on the tax invoice, the contact page,
 * and every legal page (privacy / terms / shipping / refunds), and it is also
 * the serviceable-areas distance hub (re-exported as ORIGIN_ADDRESS in
 * src/lib/originLocation.ts). Change it here and it updates everywhere.
 */
export const BUSINESS_ADDRESS =
  '1st Floor, F-3/9, Pocket F, Okhla Phase I, Okhla Industrial Estate, New Delhi, Delhi 110020';
